import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtMoney, errMsg } from "@/lib/format";
import { toast } from "sonner";
import { Calculator, Globe, RefreshCw, Loader2 } from "lucide-react";

const FLAGS = { DO: "🇩🇴", US: "🇺🇸", MX: "🇲🇽", CO: "🇨🇴", AR: "🇦🇷", CL: "🇨🇱", PE: "🇵🇪", BR: "🇧🇷" };

// Human-readable "hace X" from an ISO timestamp, for the "última actualización" line.
function timeAgo(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  const h = ms / 3_600_000;
  if (h < 1) return "hace menos de 1 hora";
  if (h < 24) return `hace ${Math.round(h)}h`;
  return `hace ${Math.round(h / 24)}d`;
}

export default function FeesPage() {
  const [fees, setFees] = useState({});
  const [country, setCountry] = useState("DO");
  const [kind, setKind] = useState("ach");
  const [amount, setAmount] = useState("10000");
  const [result, setResult] = useState(null);
  // Rates and country fees are both fetched read-only from AI/live-refreshed
  // backend caches (get_global_fx_rates / _get_current_country_fees) — there is
  // no manual entry anymore, every user always sees the same current numbers.
  const [rates, setRates] = useState({ base: "DOP", rates: {}, rates_buy: {}, rates_sell: {}, updated_at: null, source: null });
  const [ratesLoading, setRatesLoading] = useState(true);

  const loadFees = () => api.get("/fees").then(r => setFees(r.data)).catch(e => toast.error(errMsg(e)));
  const loadRates = () => {
    setRatesLoading(true);
    return api.get("/fx/current").then(r => setRates({
      base: r.data.base || "DOP",
      rates: r.data.rates || {},
      rates_buy: r.data.rates_buy || {},
      rates_sell: r.data.rates_sell || {},
      updated_at: r.data.updated_at || null,
      source: r.data.source || null,
    })).catch(() => {}).finally(() => setRatesLoading(false));
  };

  useEffect(() => { loadFees(); loadRates(); }, []);

  useEffect(() => {
    // reset kind when country changes
    const first = fees[country]?.fees?.[0]?.type;
    if (first) setKind(first);
  }, [country, fees]);

  const calc = async () => {
    if (!amount || Number(amount) <= 0) { toast.error("Monto inválido"); return; }
    try {
      const { data } = await api.post("/fees/calculate", { country, kind, amount: Number(amount) });
      setResult(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const cfg = fees[country];

  return (
    <div className="space-y-6" data-testid="fees-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Configuración</div>
        <h1 className="text-2xl sm:text-3xl font-bold mt-1">Comisiones & Divisas</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Fees calculator */}
        <div className="card-surface p-6" data-testid="fees-calc">
          <div className="flex items-center gap-2 mb-1">
            <Calculator className="w-4 h-4 text-violet-400"/>
            <h2 className="text-lg font-semibold">Calculadora de transferencias</h2>
          </div>
          <p className="text-xs text-slate-500 mb-1">Comisiones por país y tipo</p>
          <p className="text-[10px] text-slate-500 mb-5">
            {cfg?.source === "ai"
              ? `Consultadas por IA ${timeAgo(cfg.updated_at) || "recientemente"} · verifica siempre con tu banco.`
              : "Valores de referencia · verifica siempre con tu banco."}
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>País</Label>
              <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="fees-country"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
                {Object.entries(fees).map(([code, info]) => (
                  <option key={code} value={code} className="bg-[#15141b]">{FLAGS[code] || "🏳️"} {info.country}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Tipo</Label>
              <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="fees-kind"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
                {cfg?.fees?.map(f => <option key={f.type} value={f.type} className="bg-[#15141b]">{f.label}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-3">
            <Label>Monto a enviar ({cfg?.currency})</Label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="10000" data-testid="fees-amount"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-white text-lg font-semibold"/>
          </div>

          <button onClick={calc} data-testid="fees-calc-btn"
            className="mt-4 w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold py-3 rounded-xl">
            Calcular
          </button>

          {result && (
            <div className="mt-5 p-4 bg-white/[0.03] border border-white/10 rounded-xl" data-testid="fees-result">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Resultado</div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                <Metric label="Monto" value={`${cfg.currency} ${Number(result.amount).toLocaleString()}`}/>
                <Metric label="Comisión" value={`${cfg.currency} ${result.fee.toLocaleString()}`} accent="text-rose-300"/>
                <Metric label="Total a pagar" value={`${cfg.currency} ${result.total.toLocaleString()}`} accent="text-violet-300"/>
              </div>
              <div className="mt-3 text-[11px] text-slate-500">{result.rule.percent}% + {cfg.currency} {result.rule.fixed} fijo</div>
            </div>
          )}
        </div>

        {/* FX rates — read-only, always current. No manual entry: the backend
            keeps this fresh on its own (see get_global_fx_rates), so there's
            nothing for a user to forget to update. */}
        <div className="card-surface p-6" data-testid="fx-rates">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-violet-400"/>
              <h2 className="text-lg font-semibold">Tasas de cambio</h2>
            </div>
            <button onClick={loadRates} disabled={ratesLoading} data-testid="refresh-rates-btn"
              title="Volver a consultar"
              className="p-2 rounded-lg text-slate-400 hover:text-violet-300 hover:bg-white/5 transition disabled:opacity-50">
              {ratesLoading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
            </button>
          </div>
          <p className="text-xs text-slate-500 mb-1">Base: {rates.base} (1 unidad de divisa = X {rates.base})</p>
          <p className="text-[10px] text-slate-500 mb-3">
            Actualizado automáticamente {timeAgo(rates.updated_at) || "…"}{rates.source ? ` · fuente: ${rates.source}` : ""}
          </p>

          <div className="space-y-2">
            {Object.entries(rates.rates || {}).map(([code, val]) => {
              const hasSpread = code === "USDT";
              const buy = rates.rates_buy?.[code] ?? val;
              const sell = rates.rates_sell?.[code] ?? val;
              if (hasSpread) {
                return (
                  <div key={code} className="bg-white/[0.03] border border-white/5 rounded-xl px-3 py-2.5" data-testid={`rate-row-${code}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-mono font-bold w-12">{code}</span>
                      <span className="text-[10px] uppercase tracking-wider text-amber-300 font-semibold bg-amber-500/10 border border-amber-500/20 rounded-full px-2 py-0.5">
                        Compra ≠ Venta
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Compra · pagas</div>
                        <div className="flex items-center gap-1.5">
                          <span className="flex-1 min-w-0 text-sm text-right tabular-nums" data-testid={`rate-${code}-buy`}>{buy}</span>
                          <span className="text-[10px] text-slate-500">{rates.base}</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold mb-1">Venta · recibes</div>
                        <div className="flex items-center gap-1.5">
                          <span className="flex-1 min-w-0 text-sm text-right tabular-nums font-semibold text-emerald-200" data-testid={`rate-${code}-sell`}>{sell}</span>
                          <span className="text-[10px] text-slate-500">{rates.base}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1.5">
                      Tu patrimonio se calcula con el precio de <span className="text-emerald-300 font-semibold">venta</span> (lo que recibirías).
                    </div>
                  </div>
                );
              }
              return (
                <div key={code} className="flex items-center gap-2 sm:gap-3 bg-white/[0.03] border border-white/5 rounded-xl px-3 py-2.5">
                  <span className="text-sm font-mono font-bold w-12 shrink-0">{code}</span>
                  <span className="text-xs text-slate-500 shrink-0 hidden sm:inline">1 {code} =</span>
                  <span className="flex-1 min-w-0 text-sm text-right tabular-nums" data-testid={`rate-${code}`}>{val}</span>
                  <span className="text-xs text-slate-500 w-10 shrink-0 text-right">{rates.base}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1.5">{children}</div>;
}
function Metric({ label, value, accent = "text-white" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-sm font-bold mt-1 ${accent}`}>{value}</div>
    </div>
  );
}
